import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import {  Popover, List, ListItem, ListItemText } from '@mui/material';
import { ArrowDropDown } from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useGetComplaintsQuery } from '../../features/complaints/complaintsApiSlice';
import useAuth from '../../features/hooks/useAuth';
import Inbox from '../Assets/Myrequests/Inbox/Inbox';
import ConsumableInbox from '../Consumables/ConsumablesReq/Inbox/ConsumInbox';
import AMinbox from '../Assets/AssetManager/Inbox/AMinbox';
import CMinbox from '../Consumables/ConsumMan/CMinbox/CMinbox';
import CompInbox from '../Complaints/CompInbox/CompInbox';
import { useGetFilteredUnreadCountsQuery } from '../../features/assetrequest/assetReqApiSlice';
import { useGetFilteredConsumableUnreadCountsQuery } from '../../features/consumablerequest/consumableReqApiSlice';
import { assetInboxTags } from '../Assets/Myrequests/Inbox/assetInboxTags';
import { consumInboxTags } from '../Consumables/ConsumablesReq/Inbox/consumInboxTags';
import { complaintInboxTags } from '../Complaints/CompInbox/compInboxTags';

const Container = styled.div`
  padding: 20px 10px;
  background-color: #f5f5f5;
  min-height: 100vh;
`;

const TabsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  margin: 0 8px 16px 8px;
  border-bottom: 1px solid #e0e0e0;
  position: relative;
  z-index: 1;
`;

const TabButton = styled.div`
  padding: 12px 16px;
  padding-right: ${props => props.hasNotification ? '35px' : '16px'};
  cursor: pointer;
  color: ${props => props.active ? '#1976d2' : '#666'};
  font-weight: ${props => props.active ? '600' : '500'};
  font-family: 'Roboto', sans-serif;
  font-size: 1rem;
  border-bottom: ${props => props.active ? '3px solid #1976d2' : '3px solid transparent'};
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 4px;

  &:hover {
    background-color: rgba(25, 118, 210, 0.04);
    color: #1976d2;
  }
`;

const DropdownArrow = styled(ArrowDropDown)`
  font-size: 20px !important;
  transition: transform 0.2s ease;
  transform: ${props => props.open ? 'rotate(180deg)' : 'rotate(0deg)'};
  cursor: pointer;
  
  &:hover {
    background-color: rgba(25, 118, 210, 0.1);
    border-radius: 50%;
  }
`;

const NotificationBadge = styled.div`
  background-color: ${props => props.isDropdown ? '#ff5722' : '#f44336'};
  color: white;
  border-radius: 12px;
  min-width: 28px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: 600;
  position: absolute;
  top: -8px;
  right: ${props => props.isDropdown ? '-2px' : '-60px'};
  box-shadow: 0 1px 3px rgba(0,0,0,0.3);
  border: 2px solid white;
  padding: 0 4px;
  white-space: nowrap;
`;

const TabContent = styled.div`
  display: flex;
  align-items: center;
  position: relative;
`;

const TabPanel = (props) => {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      style={{ backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', overflow: 'hidden' }}
      {...other}
    >
      {value === index && children}
    </div>
  );
};

const TabCounts = (tagsObject) => {
  if (!tagsObject || typeof tagsObject !== "object") {
    return { unread: 0, total: 0 }; // safe fallback
  }

  const totalUnread = Object.values(tagsObject).reduce(
    (acc, curr) => acc + (curr?.unread || 0),
    0
  );

  const totalCount = Object.values(tagsObject).reduce(
    (acc, curr) => acc + (curr?.total || 0),
    0
  );

  return { unread: totalUnread, total: totalCount };
};


const UnifiedInbox = () => {
  const Auth = useAuth();
  const presentOffice = useSelector(state => state.office.off);
  const location = useLocation();
  
  // Set default main tab based on user type
  const getDefaultMainTab = () => {
    return Auth.supportuser ? 4 : 0; // Complaints tab for support users, Asset Request for others
  };
  
  // Set default sub tab based on user type and main tab
  const getDefaultSubTab = (mainTab) => {
    if (mainTab === 4 && Auth.supportuser) {
      // For support users on complaints tab, check roles
      const currentOfficeRoles = Auth.roles?.[presentOffice] || [];
      const mainRoles = Auth.roles?.["main"] || [];
      const allUserRoles = [...mainRoles, ...currentOfficeRoles];
      
      const isSupportStaff = allUserRoles.includes("SupportStaff");
      const isSupervisor = allUserRoles.includes("Supervisor");
      
      // If both roles exist, default to Support User (SupportStaff takes priority)
      if (isSupportStaff) {
        return 1; // Support User tab value
      } else if (isSupervisor) {
        return 2; // Support Supervisor tab value  
      }
    }
    return 0;
  };

  const defaultMainTab = getDefaultMainTab();
  const [mainTabValue, setMainTabValue] = useState(defaultMainTab);
  const [subTabValue, setSubTabValue] = useState(getDefaultSubTab(defaultMainTab));
  
  // Handle navigation from notification dropdown
  useEffect(() => {
    if (location.state?.notificationType) {
      const { notificationType, curr_sent_to_tag } = location.state;
      
      // Set the correct main tab based on notification type
      let newMainTab = mainTabValue;
      if (notificationType === 'asset') {
        newMainTab = 0; // Asset Request tab
      } else if (notificationType === 'consumable') {
        newMainTab = 1; // Consumable Request tab
      } else if (notificationType === 'complaint') {
        newMainTab = 4; // Complaints tab
      }
      
      // Find the correct sub-tab based on curr_sent_to_tag
      let newSubTab = getDefaultSubTab(newMainTab);
      if (curr_sent_to_tag) {
        // Get the appropriate inbox tags based on notification type
        const inboxTags = notificationType === 'asset' 
          ? assetInboxTags 
          : notificationType === 'consumable' 
            ? consumInboxTags 
            : complaintInboxTags;
        
        // Find which sub-tab contains this tag
        for (const [subTabIndex, tags] of Object.entries(inboxTags)) {
          if (Array.isArray(tags) && tags.includes(curr_sent_to_tag)) {
            newSubTab = parseInt(subTabIndex, 10);
            break;
          }
        }
      }
      
      if (newMainTab !== mainTabValue || newSubTab !== subTabValue) {
        setMainTabValue(newMainTab);
        setSubTabValue(newSubTab);
      }
      
      // Clear the location state after handling it
      window.history.replaceState({}, document.title);
    }
  }, [location.state, mainTabValue, subTabValue, getDefaultSubTab]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [activeTab, setActiveTab] = useState(null);
  const [resetTrigger, setResetTrigger] = useState(0);
  const [countFilter, setCountFilter] = useState({
    userId: Auth?.id,
    sent_to_tags: [],
    req_store: ""
  });
  const tabRefs = useRef([]);

  // Role-based boolean variables - Memoize to prevent infinite re-renders
  const allUserRoles = useMemo(() => {
    // For support users, Auth.roles is a simple array
    if (Auth.supportuser && Array.isArray(Auth.roles)) {
      return Auth.roles;
    }
    
    // For normal users, Auth.roles is an object with office-based structure
    const currentOfficeRoles = Auth.roles?.[presentOffice] || [];
    const mainRoles = Auth.roles?.["main"] || [];
    return [...mainRoles, ...currentOfficeRoles];
  }, [Auth.roles, presentOffice, Auth.supportuser]);

  // Create boolean variables for each role - Memoize these calculations
  const roleFlags = useMemo(() => {
    return {
      isUser: allUserRoles.includes("user"),
      isItemAdder_A: allUserRoles.includes("ItemAdder_A"),
      isL1Approver_A: allUserRoles.includes("L1Approver_A"),
      isL2Approver_A: allUserRoles.includes("L2Approver_A"),
      isL1ReqApprover_A: allUserRoles.includes("L1ReqApprover_A"),
      isL2ReqApprover_A: allUserRoles.includes("L2ReqApprover_A"),
      isItemAdder_C: allUserRoles.includes("ItemAdder_C"),
      isL1Approver_C: allUserRoles.includes("L1Approver_C"),
      isL2Approver_C: allUserRoles.includes("L2Approver_C"),
      isL1ReqApprover_C: allUserRoles.includes("L1ReqApprover_C"),
      isL2ReqApprover_C: allUserRoles.includes("L2ReqApprover_C"),
      isL1Support: allUserRoles.includes("L1Support"),
      isL2Support: allUserRoles.includes("L2Support"),
      isBo: allUserRoles.includes("Bo"),
      isUnitHead: allUserRoles.includes("UnitHead"),
      isSupportStaff: allUserRoles.includes("SupportStaff"),
      isSupervisor: allUserRoles.includes("Supervisor")
    };
  }, [allUserRoles]);

  // Sub-tabs configuration for each main tab
  const subTabs = {
    0: [ // Asset Request sub-tabs
      { label: 'User Inbox', value: 0 },
      { label: 'BO Inbox', value: 1 },
      { label: 'Unit Head Inbox', value: 2 },
      { label: 'Req ApproverL1', value: 3 },
      { label: 'Req ApproverL2', value: 4 },
      { label: 'Closed Request', value: 5 }

    ],
    1: [ // Consumable Request sub-tabs
      { label: 'User Inbox', value: 0 },
      { label: 'BO Inbox', value: 1 },
      { label: 'Unit Head Inbox', value: 2 },
      { label: 'Req ApproverL1', value: 3 },
      { label: 'Req ApproverL2', value: 4 },
      { label: 'Closed Request', value: 5 }
    ],
    2: [ // Add Asset Approval sub-tabs
      { label: 'Asset Adder', value: 0 },
      { label: 'AssetL1Approver', value: 1 },
      { label: 'AssetL2Approver', value: 2 },
      { label: 'Approved', value: 3 }
    ],
    3: [ // Add Consumable Approval sub-tabs
      { label: 'Consumable Adder', value: 0 },
      { label: 'ConsumableL1Approver', value: 1 },
      { label: 'ConsumableL2Approver', value: 2 },
      { label: 'Approved', value: 3 }
    ],
    4: [ // Complaints sub-tabs
      { label: 'User', value: 0 },
      { label: 'Support User', value: 1 },
      { label: 'Support Supervisor', value: 2 },
      { label: 'Supervisor All Complaints', value: 3 },
      { label: 'L1Support', value: 4 },
      { label: 'L2Support', value: 5 },
      { label: 'AllComplaints', value: 6 },
    ],
    5: [ // Notifications sub-tabs
      { label: 'All Notifications', value: 0 },
      { label: 'Unread', value: 1 }
    ]
  };

  const { 
    isUser, isItemAdder_A, isL1Approver_A, isL2Approver_A, isL1ReqApprover_A, isL2ReqApprover_A,
    isItemAdder_C, isL1Approver_C, isL2Approver_C, isL1ReqApprover_C, isL2ReqApprover_C,
    isL1Support, isL2Support, isBo, isUnitHead, isSupportStaff, isSupervisor
  } = roleFlags;


  // Get complaints data for the complaints tab
  const { complaints } = useGetComplaintsQuery("complaints", {
    selectFromResult: ({ data }) => ({
      complaints: data?.entities
    }),
    refetchOnFocus: true,
    refetchOnMountOrArgChange: true
  });

  const { data: filteredCounts, isLoading, error } = useGetFilteredUnreadCountsQuery(
    countFilter,
    {
      refetchOnFocus: true,
      refetchOnMountOrArgChange: true
    }
  );

  // Add consumable unread counts query
  const [consumableCountFilter, setConsumableCountFilter] = useState({
    userId: Auth?.id,
    sent_to_tags: [],
    req_store: ""
  });

  const { data: filteredConsumableCounts, isLoading: isConsumableLoading, error: consumableError } = useGetFilteredConsumableUnreadCountsQuery(
    consumableCountFilter,
    {
      refetchOnFocus: true,
      refetchOnMountOrArgChange: true
    }
  );

  console.log(countFilter)
  console.log("Filtered Counts:", filteredCounts, "Loading:", isLoading, "Error:", error);
  console.log("Consumable Counts:", filteredConsumableCounts, "Loading:", isConsumableLoading, "Error:", consumableError);

  // Fix the countFilter setup
  useEffect(() => {
    // Fetch unread counts when component mounts or countFilter changes
    setCountFilter( prev =>({
        ...prev,
        userId: Auth?.id,
        sent_to_tags: {
          0: assetInboxTags, // Asset Request tab with all sub-tabs
          2: complaintInboxTags
        },
    }))

    // Set up consumable count filter
    setConsumableCountFilter( prev =>({
        ...prev,
        userId: Auth?.id,
        sent_to_tags: {
          1: consumInboxTags, // Consumable Request tab with all sub-tabs
        },
    }))
  }, []);

  // Helper functions to get counts from the new API structure
  const getTabCounts = useCallback((tabValue) => {
    let counts;
    
    // Use appropriate count data based on tab value
    if (tabValue === 1) {
      // Consumable Request tab
      counts = filteredConsumableCounts;
    } else {
      // Asset Request and other tabs
      counts = filteredCounts;
    }
    
    if (!counts || !counts[tabValue]) {
      return { unread: 0, total: 0 };
    }
    
    // Aggregate counts from all sub-tabs for this main tab
    let totalUnread = 0;
    let totalCount = 0;
    
    Object.values(counts[tabValue]).forEach(subTabData => {
      totalUnread += subTabData.unread || 0;
      totalCount += subTabData.total || 0;
    });
    
    return { unread: totalUnread, total: totalCount };
  }, [filteredCounts, filteredConsumableCounts]);

  const getSubTabCounts = useCallback((mainTabValue, subTabValue) => {
    let counts;
    
    // Use appropriate count data based on tab value
    if (mainTabValue === 1) {
      // Consumable Request tab
      counts = filteredConsumableCounts;
    } else {
      // Asset Request and other tabs
      counts = filteredCounts;
    }
    
    if (!counts || !counts[mainTabValue] || !counts[mainTabValue][subTabValue]) {
      return { unread: 0, total: 0 };
    }
    
    const subTabData = counts[mainTabValue][subTabValue];
    return {
      unread: subTabData.unread || 0,
      total: subTabData.total || 0
    };
  }, [filteredCounts, filteredConsumableCounts]);

  // Format badge text as "unread/total" or just the number if they're the same
  const formatBadgeText = useCallback((unread, total) => {
    if (unread === 0 && total === 0) return null;
    if (unread === total) return unread.toString();
    return `${unread}/${total}`;
  }, []);

  // Main tabs configuration
  // const mainTabs = [
  //   { label: 'Asset Request', value: 0 },
  //   { label: 'Consumable Request', value: 1 },
  //   { label: 'Add Asset Approval', value: 2 },
  //   { label: 'Add Consumable Approval', value: 3 },
  //   { label: 'Complaints', value: 4 },
  //   { label: 'Notifications', value: 5 }
  // ];

  // Filter tabs based on user roles - Memoize this expensive calculation
  const visibleTabs = useMemo(() => {
    let tabs = [];

    // If user has only "user" role (no other roles) and is not a support user
    if (allUserRoles.length === 1 && isUser && !Auth.supportuser) {
      return [
        { label: 'Asset Request', value: 0 },
        { label: 'Consumable Request', value: 1 },
        { label: 'Complaints', value: 4 },
        { label: 'Notifications', value: 5 }
      ];
    }

    // For users with additional roles, build tabs based on their specific roles

    // Asset Request tab - show if user has any asset-related roles or basic user access
    if (isUser || isBo || isUnitHead || isL1ReqApprover_A || isL2ReqApprover_A || isItemAdder_A || isL1Approver_A || isL2Approver_A) {
      tabs.push({ label: 'Asset Request', value: 0 });
    }

    // Consumable Request tab - show if user has any consumable-related roles or basic user access
    if (isUser || isBo || isUnitHead || isL1ReqApprover_C || isL2ReqApprover_C || isItemAdder_C || isL1Approver_C || isL2Approver_C) {
      tabs.push({ label: 'Consumable Request', value: 1 });
    }

    // Add Asset Approval tab - show only if user has asset approval-related roles
    // if (isItemAdder_A || isL1Approver_A || isL2Approver_A) {
    //   tabs.push({ label: 'Add Asset Approval', value: 2 });
    // }

    // Add Consumable Approval tab - show only if user has consumable approval-related roles
    // if (isItemAdder_C || isL1Approver_C || isL2Approver_C) {
    //   tabs.push({ label: 'Add Consumable Approval', value: 3 });
    // }

    // Complaints tab - show for all users with any role
    tabs.push({ label: 'Complaints', value: 4 });

    // Notifications tab - show for all users with any role
    // tabs.push({ label: 'Notifications', value: 5 });

    return tabs;
  }, [allUserRoles, isUser, isBo, isUnitHead, isL1ReqApprover_A, isL2ReqApprover_A, isItemAdder_A, isL1Approver_A, isL2Approver_A, isL1ReqApprover_C, isL2ReqApprover_C, isItemAdder_C, isL1Approver_C, isL2Approver_C]);

  const hasOnlyUserRole = allUserRoles.length === 1 && isUser;

  

  // Filter sub-tabs based on user roles - Memoize this function
  const getVisibleSubTabs = useCallback((tabValue) => {
    const allSubTabs = subTabs[tabValue] || [];

    // If user has only "user" role (not support users), no sub-tabs needed
    if (allUserRoles.length === 1 && isUser && !Auth.supportuser) {
      return [];
    }

    // For users with roles other than just "user", always include User Inbox
    let visibleSubTabs = [];

    // Always add User Inbox for Asset Request and Consumable Request when user has additional roles
    if (tabValue === 0 || tabValue === 1) { // Asset Request or Consumable Request
      const userInboxTab = allSubTabs.find(subTab => subTab.label === 'User Inbox');
      if (userInboxTab) {
        visibleSubTabs.push(userInboxTab);
      }
    }

    // Add role-specific tabs for Asset Request and Consumable Request
    if (tabValue === 0 || tabValue === 1) { // Asset Request or Consumable Request

      // If user has Bo role, add BO Inbox
      if (isBo) {
        const boInboxTab = allSubTabs.find(subTab => subTab.label === 'BO Inbox');
        if (boInboxTab) {
          visibleSubTabs.push(boInboxTab);
        }
      }

      // If user has UnitHead role, add Unit Head Inbox
      if (isUnitHead) {
        const unitHeadInboxTab = allSubTabs.find(subTab => subTab.label === 'Unit Head Inbox');
        if (unitHeadInboxTab) {
          visibleSubTabs.push(unitHeadInboxTab);
        }
      }

      // If user has L1ReqApprover_A role and it's Asset Request, add Req ApproverL1
      if (isL1ReqApprover_A && tabValue === 0) {
        const l1ApproverTab = allSubTabs.find(subTab => subTab.label === 'Req ApproverL1');
        if (l1ApproverTab) {
          visibleSubTabs.push(l1ApproverTab);
        }
      }

      // If user has L2ReqApprover_A role and it's Asset Request, add Req ApproverL2
      if (isL2ReqApprover_A && tabValue === 0) {
        const l2ApproverTab = allSubTabs.find(subTab => subTab.label === 'Req ApproverL2');
        if (l2ApproverTab) {
          visibleSubTabs.push(l2ApproverTab);
        }
      }

      // If user has L1ReqApprover_C role and it's Consumable Request, add Req ApproverL1
      if (isL1ReqApprover_C && tabValue === 1) {
        const l1ApproverTab = allSubTabs.find(subTab => subTab.label === 'Req ApproverL1');
        if (l1ApproverTab) {
          visibleSubTabs.push(l1ApproverTab);
        }
      }

      // If user has L2ReqApprover_C role and it's Consumable Request, add Req ApproverL2
      if (isL2ReqApprover_C && tabValue === 1) {
        const l2ApproverTab = allSubTabs.find(subTab => subTab.label === 'Req ApproverL2');
        if (l2ApproverTab) {
          visibleSubTabs.push(l2ApproverTab);
        }
      }
    }

    // Handle Complaints tab (tabValue === 4)
    if (tabValue === 4) {
      // Check if user is a support user
      if (Auth.supportuser) {
        // For support users, only show Support User and Support Supervisor subtabs
        if (isSupportStaff) {
          const supportUserTab = allSubTabs.find(subTab => subTab.label === 'Support User');
          if (supportUserTab) {
            visibleSubTabs.push(supportUserTab);
          }
        }

        if (isSupervisor) {
          const supportSupervisorTab = allSubTabs.find(subTab => subTab.label === 'Support Supervisor');
          if (supportSupervisorTab) {
            visibleSubTabs.push(supportSupervisorTab);
          }
          // Add Supervisor All Complaints tab (view-only)
          const supervisorAllComplaintsTab = allSubTabs.find(subTab => subTab.label === 'Supervisor All Complaints');
          if (supervisorAllComplaintsTab) {
            visibleSubTabs.push(supervisorAllComplaintsTab);
          }
        }
      } else {
        // For normal users, keep the existing logic
        // Always add User for users with any role
        const userTab = allSubTabs.find(subTab => subTab.label === 'User');
        if (userTab) {
          visibleSubTabs.push(userTab);
        }

        // If user has L1Support role, add L1Support
        if (isL1Support) {
          const l1SupportTab = allSubTabs.find(subTab => subTab.label === 'L1Support');
          if (l1SupportTab) {
            visibleSubTabs.push(l1SupportTab);
          }
        }

        // If user has L2Support role, add L2Support
        if (isL2Support) {
          const l2SupportTab = allSubTabs.find(subTab => subTab.label === 'L2Support');
          if (l2SupportTab) {
            visibleSubTabs.push(l2SupportTab);
          }
        }

        // If user has any support role, add AllComplaints (no Closed in Inbox)
        if (isL1Support || isL2Support) {
          const allComplaintsTab = allSubTabs.find(subTab => subTab.label === 'AllComplaints');
          if (allComplaintsTab) {
            visibleSubTabs.push(allComplaintsTab);
          }
        }
      }
    }

    // Handle Notifications tab (tabValue === 5) - return default sub-tabs
    if (tabValue === 5) {
      return allSubTabs;
    }

    // Handle Add Asset Approval tab (tabValue === 2)
    if (tabValue === 2) {
      // If user has ItemAdder_A role, add Asset Adder
      if (isItemAdder_A) {
        const assetAdderTab = allSubTabs.find(subTab => subTab.label === 'Asset Adder');
        if (assetAdderTab) {
          visibleSubTabs.push(assetAdderTab);
        }
      }

      // If user has L1Approver_A role, add AssetL1Approver
      if (isL1Approver_A) {
        const l1ApproverTab = allSubTabs.find(subTab => subTab.label === 'AssetL1Approver');
        if (l1ApproverTab) {
          visibleSubTabs.push(l1ApproverTab);
        }
      }

      // If user has L2Approver_A role, add AssetL2Approver
      if (isL2Approver_A) {
        const l2ApproverTab = allSubTabs.find(subTab => subTab.label === 'AssetL2Approver');
        if (l2ApproverTab) {
          visibleSubTabs.push(l2ApproverTab);
        }
      }

      // Add Approved tab for users with any of these roles
      if (isItemAdder_A || isL1Approver_A || isL2Approver_A) {
        const approvedTab = allSubTabs.find(subTab => subTab.label === 'Approved');
        if (approvedTab) {
          visibleSubTabs.push(approvedTab);
        }
      }
    }

    // Handle Add Consumable Approval tab (tabValue === 3)
    if (tabValue === 3) {
      // If user has ItemAdder_C role, add Consumable Adder
      if (isItemAdder_C) {
        const consumableAdderTab = allSubTabs.find(subTab => subTab.label === 'Consumable Adder');
        if (consumableAdderTab) {
          visibleSubTabs.push(consumableAdderTab);
        }
      }

      // If user has L1Approver_C role, add ConsumableL1Approver
      if (isL1Approver_C) {
        const l1ApproverTab = allSubTabs.find(subTab => subTab.label === 'ConsumableL1Approver');
        if (l1ApproverTab) {
          visibleSubTabs.push(l1ApproverTab);
        }
      }

      // If user has L2Approver_C role, add ConsumableL2Approver
      if (isL2Approver_C) {
        const l2ApproverTab = allSubTabs.find(subTab => subTab.label === 'ConsumableL2Approver');
        if (l2ApproverTab) {
          visibleSubTabs.push(l2ApproverTab);
        }
      }

      // Add Approved tab for users with any of these roles
      if (isItemAdder_C || isL1Approver_C || isL2Approver_C) {
        const approvedTab = allSubTabs.find(subTab => subTab.label === 'Approved');
        if (approvedTab) {
          visibleSubTabs.push(approvedTab);
        }
      }
    }

    // Remove duplicates and return
    return visibleSubTabs.filter((subTab, index, self) =>
      index === self.findIndex(t => t.value === subTab.value)
    );
  }, [allUserRoles, isUser, isBo, isUnitHead, isL1ReqApprover_A, isL2ReqApprover_A, isItemAdder_A, isL1Approver_A, isL2Approver_A, isL1ReqApprover_C, isL2ReqApprover_C, isItemAdder_C, isL1Approver_C, isL2Approver_C, isL1Support, isL2Support, isSupportStaff, isSupervisor]);

  // Handle direct clicks on main tabs
  const handleMainTabClick = useCallback((newValue) => {
    setMainTabValue(newValue);
    
    // For support users on complaints tab, set default subtab based on roles
    if (newValue === 4 && Auth.supportuser) {
      // If both roles exist, default to Support User (SupportStaff takes priority)
      if (isSupportStaff) {
        setSubTabValue(1); // Support User tab value
      } else if (isSupervisor) {
        setSubTabValue(2); // Support Supervisor tab value  
      } else {
        setSubTabValue(0);
      }
    } else {
      setSubTabValue(0);
    }
    
    setActiveTab(null);
    setAnchorEl(null);
    setResetTrigger(prev => prev + 1);

  }, [Auth.supportuser, isSupportStaff, isSupervisor]);

  const handleSubTabChange = useCallback((subTabIndex) => {
    setSubTabValue(subTabIndex);
    setAnchorEl(null);
    setActiveTab(null);
    setResetTrigger(prev => prev + 1);

  }, []);

  const handleDropdownClick = useCallback((event, tabIndex) => {
    event.stopPropagation(); // Prevent tab click

    // If user has only "user" role (not support users), no dropdown needed
    if (allUserRoles.length === 1 && isUser && !Auth.supportuser) return;

    // Get the tab value from the visible tabs array using the index
    const tabValue = visibleTabs[tabIndex]?.value;
    if (tabValue === undefined) return;

    // Check if the tab has sub-tabs
    const visibleSubTabs = getVisibleSubTabs(tabValue);
    if (visibleSubTabs.length <= 1) return;

    // Toggle dropdown
    if (activeTab === tabValue && anchorEl) {
      // Close dropdown if clicking the same arrow
      setActiveTab(null);
      setAnchorEl(null);
    } else {
      // Open dropdown for this tab - use the tab element as anchor, not the arrow
      setActiveTab(tabValue);
      setAnchorEl(tabRefs.current[tabIndex]);
    }
  }, [activeTab, anchorEl, allUserRoles, isUser, visibleTabs, getVisibleSubTabs]);

  // Initialize refs array for tabs
  useEffect(() => {
    tabRefs.current = tabRefs.current.slice(0, visibleTabs.length);
  }, [visibleTabs]);

  // Initialize correct tab values for support users on mount ONLY
  useEffect(() => {
    if (Auth.supportuser && Auth.roles && Array.isArray(Auth.roles)) {
      const isSupportStaff = Auth.roles.includes("SupportStaff");
      const isSupervisor = Auth.roles.includes("Supervisor");
      
      // Set Complaints as default main tab
      if (mainTabValue !== 4) {
        setMainTabValue(4);
      }
      
      // Set appropriate subtab based on roles
      let correctSubTabValue = 0;
      if (isSupportStaff) {
        correctSubTabValue = 1; // Support User tab
      } else if (isSupervisor) {
        correctSubTabValue = 2; // Support Supervisor tab
      }
      
      if (subTabValue !== correctSubTabValue) {
        setSubTabValue(correctSubTabValue);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run ONLY on mount, not when tabs change

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      const isOpen = Boolean(anchorEl) && activeTab !== null;
      if (isOpen && !event.target.closest('.MuiPopover-root') && !event.target.closest('[data-dropdown-arrow]')) {
        setActiveTab(null);
        setAnchorEl(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [anchorEl, activeTab]);

  const getTabLabel = useCallback((tab) => { 
    // For users with only "user" role (not support users), don't show sub-tab labels
    if (allUserRoles.length === 1 && isUser && !Auth.supportuser) {
      const { unread, total } = getTabCounts(tab.value);
      return (
        <TabContent>
          <span>{tab.label}</span>
          { (unread > 0 || total > 0) ? <NotificationBadge>{`${unread}/${total}`}</NotificationBadge>: null}
        </TabContent>
      );
    }
    
    // For users with additional roles, show subtab label in brackets
    let displayLabel = tab.label;
    if (mainTabValue === tab.value) {
      const visibleSubTabs = getVisibleSubTabs(tab.value);
      // Find the sub-tab by its value property instead of using index
      const selectedSubTab = visibleSubTabs.find(subTab => subTab.value === subTabValue);
      if (selectedSubTab) {
        displayLabel = `${tab.label} (${selectedSubTab.label})`;
      }
    }

    const { unread, total } = getTabCounts(tab.value);
    return (
      <TabContent>
        <span>{displayLabel}</span>
        { (unread > 0 || total > 0) ? <NotificationBadge>{`${unread}/${total}`}</NotificationBadge>: null}
      </TabContent>
    );
  }, [allUserRoles, isUser, mainTabValue, subTabValue, getVisibleSubTabs, getTabCounts, getSubTabCounts, formatBadgeText, Auth.supportuser]);

  // Memoize dropdown logic to prevent inline function recreation
  const renderDropdownArrow = useCallback((tab, index) => {
    const visibleSubTabs = getVisibleSubTabs(tab.value);
    const hasDropdown = !(allUserRoles.length === 1 && isUser && !Auth.supportuser) && visibleSubTabs.length > 1;

    return hasDropdown ? (
      <DropdownArrow
        open={activeTab === tab.value && Boolean(anchorEl)}
        onClick={(e) => handleDropdownClick(e, index)}
        data-dropdown-arrow="true"
      />
    ) : null;
  }, [allUserRoles, isUser, getVisibleSubTabs, activeTab, anchorEl, handleDropdownClick]);

  // Memoize popover sub-tab items to prevent recreation
  const renderSubTabItems = useCallback(() => {
    if (activeTab === null) return null;
    
    return getVisibleSubTabs(activeTab).map((subTab) => (
      <ListItem
        key={subTab.value}
        button
        selected={mainTabValue === activeTab && subTabValue === subTab.value}
        onClick={() => {
          if (mainTabValue !== activeTab) {
            setMainTabValue(activeTab);
          }
          handleSubTabChange(subTab.value);
        }}
        sx={{
          py: 1.5,
          backgroundColor: (mainTabValue === activeTab && subTabValue === subTab.value)
            ? 'rgba(25, 118, 210, 0.08)'
            : 'transparent',
          '&:hover': {
            backgroundColor: 'rgba(25, 118, 210, 0.12)'
          }
        }}
      >
        <ListItemText
          primary={
            <TabContent>
              <span>{subTab.label}</span>
              {(() => {
                const { unread, total } = getSubTabCounts(activeTab, subTab.value);
                const badgeText = formatBadgeText(unread, total);
                return badgeText ? <NotificationBadge isDropdown={true}>{badgeText}</NotificationBadge> : null;
              })()}
            </TabContent>
          }
          primaryTypographyProps={{
            fontSize: '0.9rem',
            fontWeight: (mainTabValue === activeTab && subTabValue === subTab.value) ? 600 : 400
          }}
        />
      </ListItem>
    ));
  }, [activeTab, mainTabValue, subTabValue, getVisibleSubTabs, handleSubTabChange, getSubTabCounts, formatBadgeText]);

  const open = Boolean(anchorEl) && activeTab !== null;

  return (
    <Container>
      {/* Main Tabs */}
      <TabsContainer>
        {visibleTabs.map((tab, index) => (
          <TabButton
            key={tab.value}
            ref={el => tabRefs.current[index] = el}
            active={mainTabValue === tab.value}
            onClick={() => handleMainTabClick(tab.value)}
            hasNotification={(() => {
              const { unread, total } = getTabCounts(tab.value);
              return unread > 0 || total > 0;
            })()}
          >
            {getTabLabel(tab)}
            {renderDropdownArrow(tab, index)}
          </TabButton>
        ))}
      </TabsContainer>

      {/* Sub-tabs Popover */}
      <Popover
        open={open && !(allUserRoles.length === 1 && isUser && !Auth.supportuser)}
        anchorEl={anchorEl}
        onClose={() => setActiveTab(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        disableRestoreFocus
        sx={{
          pointerEvents: 'auto',
          mt: 0.5,
          '& .MuiPopover-paper': {
            minWidth: activeTab === 3 ? 250 : 200,
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            border: '1px solid #e0e0e0',
            borderRadius: '8px',
          }
        }}
        disableAutoFocus
        disableEnforceFocus
      >
        <List
          dense
          sx={{
            p: 0,
            '& .MuiListItem-root': {
              borderBottom: '1px solid #f0f0f0',
              padding: '12px 16px',
              '&:last-child': {
                borderBottom: 'none'
              },
              '&:hover': {
                backgroundColor: 'rgba(25, 118, 210, 0.08)'
              }
            }
          }}
        >
          {renderSubTabItems()}
        </List>
      </Popover>

      {/* Tab Panels */}
      <TabPanel value={mainTabValue} index={0}>
        {/* Asset Request Tab Content */}
        <Inbox subTabValue={subTabValue} resetTrigger={resetTrigger} />
      </TabPanel>

      <TabPanel value={mainTabValue} index={1}>
        {/* Consumable Request Tab Content */}
        <ConsumableInbox subTabValue={subTabValue} resetTrigger={resetTrigger} />
      </TabPanel>

      <TabPanel value={mainTabValue} index={2}>
        {/* Add Asset Approval Tab Content */}
        <AMinbox subTabValue={subTabValue} resetTrigger={resetTrigger} />
      </TabPanel>

      <TabPanel value={mainTabValue} index={3}>
        {/* Add Consumable Approval Tab Content */}
        <CMinbox subTabValue={subTabValue} resetTrigger={resetTrigger} />
      </TabPanel>

      <TabPanel value={mainTabValue} index={4}>
        {/* Complaints Tab Content */}
        <CompInbox subTabValue={subTabValue} resetTrigger={resetTrigger} />
      </TabPanel>

      <TabPanel value={mainTabValue} index={5}>
        {/* Notifications Tab Content */}
        <div>Notifications content will go here</div>
      </TabPanel>
    </Container>
  );
};

export default UnifiedInbox; 